import type { Common } from "@beautypie/segment";
import cookie from "js-cookie";

import type { CountryType } from "@shared/graphql.apollo";
import { Logger } from "lib/logger";
import { getSession } from "session";
import { getInitialAnonId } from "@shared/utils.user";
import { getBPFingerprint } from "lib/fingerprint";
import { parseCampaign } from "./campaign";
import { parseDevice } from "./device";
import { parseTraits } from "./traits";
import { parseOS } from "./os";
import { parsePageDetails } from "./page";
import { isMigrated } from "./identifier";
import { searchTermFromURL } from "./search";
import { parseExperimentFlags } from "./experiment-flags";
import { getQueryParams } from "./queryParams";
import {
  EXPERIENCE_ID_COOKIE,
  TEST_IDENTITY_ID_COOKIE
} from "@shared/utils.cookies";

export const getContext = async (
  locale: CountryType
): Promise<Common["context"] | undefined> => {
  const store = locale === "us" ? "BeautyPie US" : "BeautyPie UK";
  const [session, traits] = await Promise.all([getSession(), parseTraits()]);

  const sessionId = session?.id;
  const visitNumber = session?.count;
  // The legacySessionId is required only as long as the data team is moving across to use fingerprint as the long term identifier of users for reporting purposes. It exists side-by-side with the newer, fingerprint derived, sessionId on all events
  const legacySessionId = session
    ? `${getInitialAnonId()}:${visitNumber}`
    : undefined;

  const log = Logger("data/tracking/segment/getContext");

  const searchTerm = searchTermFromURL();
  const queryStringObject = getQueryParams(window.location.href);

  try {
    return {
      locale,
      store,
      event_id: "",
      fbp: cookie.get("_fbp"),
      fbc: cookie.get("_fbc"),
      ttp: cookie.get("_ttp"),
      ttclid: cookie.get("ttclid"),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: navigator?.userAgent,
      campaign: parseCampaign(),
      device: parseDevice(),
      os: parseOS(),
      page: parsePageDetails(locale),
      traits,
      ...(isMigrated() ? { migrated: true } : {}),
      fingerprintId: await getBPFingerprint().then(
        (result) => result?.fingerprintId
      ),
      sessionId,
      visitNumber,
      // @todo: remove this once confirmed by all teams
      experiementFlags: parseExperimentFlags(),
      legacySessionId,
      queryStringObject,
      experienceId: cookie.get(EXPERIENCE_ID_COOKIE),
      testIdentity: cookie.get(TEST_IDENTITY_ID_COOKIE),
      platform: "web",

      ...(searchTerm
        ? {
            search: {
              term: searchTerm
            }
          }
        : {})
    };
  } catch (e) {
    log.error(`Error retrieving context: ${JSON.stringify(e)}`);
  }
};
